/**
 * The task board (§12.1, P4-BR3).
 *
 * Everyone sees the whole board, which is the point — a list filtered to your
 * own jobs cannot answer "what is outstanding on this farm". Overdue work is
 * pulled to the top and marked, because that is the only thing on this screen
 * worth interrupting somebody about.
 *
 * The "done" button is shown to everyone and refused by the server when P4-BR3
 * says so. Hiding it client-side would be a nicer first impression and a worse
 * rule: the browser is not where permission is decided, and a screen that
 * guesses will eventually guess wrong in the permissive direction.
 */

import { useState } from 'react';

import { useTranslation } from '../../i18n/useTranslation.js';
import { errorMessageKey } from '../../lib/apiError.js';
import { usePartners } from '../finance/api.js';
import { useCompleteTask, useCreateTask, useReopenTask, useTasks, type Task } from './api.js';

function TaskRow({ task }: { task: Task }): JSX.Element {
  const { t } = useTranslation();
  const complete = useCompleteTask();
  const reopen = useReopenTask();
  const [error, setError] = useState<string | null>(null);

  const isDone = task.status === 'done';
  const busy = complete.isPending || reopen.isPending;

  const toggle = (): void => {
    setError(null);
    const action = isDone ? reopen : complete;

    action.mutate(task.id, { onError: (err) => setError(t(errorMessageKey(err))) });
  };

  return (
    <li className={`rounded-xl bg-white px-4 py-3 shadow-sm ${task.isOverdue ? 'border-l-4 border-red-500' : ''}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className={`text-sm font-medium ${isDone ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
            {task.title}
          </p>

          <p className="mt-0.5 text-xs text-gray-500">
            {task.assignedToName ?? t('tasks.unassigned')}
            {task.dueDate !== null && ` · ${task.dueDate}`}
          </p>

          {task.isOverdue && <p className="mt-1 text-xs font-medium text-red-700">{t('tasks.overdue')}</p>}
          {task.status === 'cancelled' && (
            <p className="mt-1 text-xs text-gray-500">{t('tasks.cancelledFor', { reason: task.cancelReason ?? '' })}</p>
          )}
        </div>

        {task.status !== 'cancelled' && (
          <button
            type="button"
            onClick={toggle}
            disabled={busy}
            className={`min-h-touch shrink-0 rounded-lg px-3 py-2 text-xs font-medium ${
              isDone ? 'bg-gray-100 text-gray-600' : 'bg-green-700 text-white'
            }`}
          >
            {isDone ? t('tasks.reopen') : t('tasks.markDone')}
          </button>
        )}
      </div>

      {error !== null && (
        <p role="alert" className="mt-2 text-xs text-red-700">
          {error}
        </p>
      )}
    </li>
  );
}

export function TaskBoard(): JSX.Element {
  const { t } = useTranslation();
  const [showDone, setShowDone] = useState(false);
  const { data: tasks, isLoading, isError, refetch } = useTasks();
  const { data: partners } = usePartners();

  const [title, setTitle] = useState('');
  const [assignedToId, setAssignedToId] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const createTask = useCreateTask();

  const submit = (event: React.FormEvent): void => {
    event.preventDefault();
    setFormError(null);

    createTask.mutate(
      {
        title: title.trim(),
        ...(assignedToId !== '' ? { assignedToId: Number(assignedToId) } : {}),
        ...(dueDate !== '' ? { dueDate } : {}),
      },
      {
        onSuccess: () => {
          setTitle('');
          setAssignedToId('');
          setDueDate('');
        },
        onError: (err) => setFormError(t(errorMessageKey(err))),
      },
    );
  };

  const visible = (tasks ?? []).filter((task) => showDone || task.status === 'pending');

  return (
    <div className="page-container">
      <h1 className="py-4 text-xl font-semibold text-gray-800">{t('tasks.title')}</h1>

      <form onSubmit={submit} className="mb-4 rounded-xl bg-white p-4 shadow-sm">
        <label className="block text-xs text-gray-500" htmlFor="task-title">
          {t('tasks.newTask')}
        </label>
        <input
          id="task-title"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          required
          minLength={3}
          maxLength={200}
          className="mt-1 min-h-touch w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
        />

        <div className="mt-2 grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs text-gray-500" htmlFor="task-assignee">
              {t('tasks.assignTo')}
            </label>
            <select
              id="task-assignee"
              value={assignedToId}
              onChange={(event) => setAssignedToId(event.target.value)}
              className="mt-1 min-h-touch w-full rounded-lg border border-gray-300 px-2 py-2 text-sm"
            >
              <option value="">{t('tasks.unassigned')}</option>
              {(partners ?? []).map((partner) => (
                <option key={partner.partnerId} value={partner.partnerId}>
                  {partner.fullName}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs text-gray-500" htmlFor="task-due">
              {t('tasks.dueDate')}
            </label>
            <input
              id="task-due"
              type="date"
              value={dueDate}
              onChange={(event) => setDueDate(event.target.value)}
              className="mt-1 min-h-touch w-full rounded-lg border border-gray-300 px-2 py-2 text-sm"
            />
          </div>
        </div>

        {formError !== null && (
          <p role="alert" className="mt-2 text-xs text-red-700">
            {formError}
          </p>
        )}

        <button
          type="submit"
          disabled={createTask.isPending || title.trim().length < 3}
          className="mt-3 min-h-touch w-full rounded-lg bg-green-700 px-4 py-2 text-sm font-medium text-white disabled:bg-gray-300"
        >
          {t('tasks.add')}
        </button>
      </form>

      <label className="mb-2 flex items-center gap-2 text-sm text-gray-600">
        <input type="checkbox" checked={showDone} onChange={(event) => setShowDone(event.target.checked)} />
        {t('tasks.showDone')}
      </label>

      {isLoading && (
        <p role="status" className="py-8 text-center text-gray-500">
          {t('app.loading')}
        </p>
      )}

      {isError && (
        <div role="alert" className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-800">
          <p className="mb-2">{t('error.network')}</p>
          <button type="button" onClick={() => void refetch()} className="min-h-touch font-medium underline">
            {t('app.retry')}
          </button>
        </div>
      )}

      {tasks !== undefined && visible.length === 0 && (
        <p className="py-8 text-center text-sm text-gray-500">{t('tasks.empty')}</p>
      )}

      <ul className="space-y-2">
        {visible.map((task) => (
          <TaskRow key={task.id} task={task} />
        ))}
      </ul>
    </div>
  );
}
